import { API_URL } from '@constants';
import { useData } from '@hooks/useData';
import { useApiConfig } from '@hooks/useApiConfig';
import { SpaceItem } from './useGetSpaces';
import { useState, useCallback } from 'react';

export type { SpaceItem };

const ENDPOINT_URL = '/spaces';

export interface GetSpaceDetailSuccessResponse {
    status: number;
    success: boolean;
    message: string;
    data: SpaceItem;
    links: {
        self: string;
    };
}

interface ApiErrorResponse {
    status: number;
    success: boolean;
    message: string;
    data: null;
}

export interface SpaceDetailError {
    httpStatus: number | null;
    message: string;
    isNotFound: boolean;
    isNetworkError: boolean;
}

/**
 * Requests endpoint to get a single space by UUID.
 * Then saves it in Data Context.
 */
export const useGetSpaceDetail = () => {
    const { getHeaders, handleCommonErrors } = useApiConfig();
    const { setSpaceDetail, setIsLoadingSpaceDetail } = useData();
    const [error, setError] = useState<SpaceDetailError | null>(null);

    const request = useCallback(async (uuid: string) => {
        setIsLoadingSpaceDetail(true);
        setError(null);

        try {
            const response = await fetch(`${API_URL}${ENDPOINT_URL}/${uuid}`, {
                method: 'GET',
                headers: getHeaders(),
            });

            if (!response.ok) {
                const json: ApiErrorResponse = await response.json();

                setSpaceDetail(null);
                setError({
                    httpStatus: response.status,
                    message: json.message,
                    isNotFound: response.status === 404,
                    isNetworkError: false,
                });

                if (response.status === 401) {
                    handleCommonErrors({ statusCode: 401, name: 'ApiError', message: json.message, errors: null });
                }
                return;
            }

            const data: GetSpaceDetailSuccessResponse = await response.json();
            setSpaceDetail(data);
        } catch (err) {
            // Erro de rede/timeout - fetch nao conseguiu completar a requisicao
            setSpaceDetail(null);
            setError({
                httpStatus: null,
                message: err instanceof Error ? err.message : 'Erro de rede',
                isNotFound: false,
                isNetworkError: true,
            });
        } finally {
            setIsLoadingSpaceDetail(false);
        }
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const clearError = useCallback(() => {
        setError(null);
    }, []);

    return { request, error, clearError };
};
